import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { fmtDuration } from '@vitesse/base-ui/format';
import { button } from '@vitesse/base-ui/components/base-buttons';
import '@vitesse/base-ui/elements/vitesse-pill-tabs';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';
import type { BackendClient } from '../../bridge/backend-client';
import type { ControllerOpen } from '../../shell/router';
import { downloadHistoryCsv } from '../../bridge/history-export';
import { HistoryViewportController, type ViewportChangeCause } from './history-viewport-controller';
import { CHART_THEMES, type ThemeOption } from '../chart/chart-types';
import {
  clampToDataStart,
  lowerBound,
  MAX_VIEWPORT_SPAN_MS,
  MIN_VIEWPORT_SPAN_MS,
  presetExceedsLifespan,
  type TimelineViewport,
} from '../chart/timeline-types';
import '../chart/vitesse-timeline-chart';
import type { TimelineLane, VitesseTimelineChart } from '../chart/vitesse-timeline-chart';
import './history-symbol-picker';
import './history-combo-dialog';

/** How many symbols may stack as lanes. Three keeps every lane tall enough to
 *  read at the chartwrap's 340px floor; it is a UI budget, not a data limit. */
export const MAX_HISTORY_SYMBOLS = 3;

/** Lane colors by SLOT (not by symbol): frost/mint/candy are the maximally
 *  separated trio of the shared chart palette (the precedent is ai-ui's
 *  SERIES_THEMES). Slot-stable: remove the middle lane and the survivors keep
 *  their position's color, so the legend chips always match the strips. */
const LANE_THEMES: readonly ThemeOption[] = ['frost', 'mint', 'candy'];

/** Quick ranges — the fast path. Direct drag/wheel + the absolute picker are
 *  the other two ways to move; none of the three is the only one. */
const PRESETS: readonly { id: string; labelKey: string; spanMs: number }[] = [
  { id: '15m', labelKey: 'controller.historyRange15m', spanMs: 15 * 60_000 },
  { id: '1h', labelKey: 'controller.historyRange1h', spanMs: 3_600_000 },
  { id: '1d', labelKey: 'controller.historyRange1d', spanMs: 86_400_000 },
  { id: '1w', labelKey: 'controller.historyRange1w', spanMs: 7 * 86_400_000 },
  { id: '1mo', labelKey: 'controller.historyRange1mo', spanMs: 30 * 86_400_000 },
  { id: '1y', labelKey: 'controller.historyRange1y', spanMs: 365 * 86_400_000 },
];

const DEFAULT_SPAN_MS = PRESETS[0].spanMs;

/** How long a fetch failure must PERSIST before the error chip shows — long
 *  enough for the 2s failure-retry to succeed first, so a one-off timeout
 *  never flashes red (it reads as still-loading meanwhile). */
const FAILED_CHIP_DELAY_MS = 1000;

/** datetime-local wants local wall time as YYYY-MM-DDTHH:mm. */
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Same, with seconds (pair with step="1" on the input). The EXPORT dialog
 *  needs this: a viewport zoomed under a minute truncates to the SAME minute
 *  at minute precision, from == to, and the range validation refuses — which
 *  read as "the Download button does nothing." */
function toLocalInputSec(ms: number): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${toLocalInput(ms)}:${p(new Date(ms).getSeconds())}`;
}

/*
 * <vitesse-controller-history> — the History tab: one full-bleed explorable
 * timeline of USER-ASSEMBLED lanes (up to MAX_HISTORY_SYMBOLS symbols, each a
 * strip on a shared time axis — the Perfetto shape, with the selection in the
 * user's hands). The chart is a controlled canvas (<vitesse-timeline-chart>)
 * that emits gesture intents; ONE HistoryViewportController per symbol owns
 * that lane's resolution ladder + bounded prefetch cache and does all IO
 * through the Backend Worker. This element owns the SHARED viewport and the
 * shared frozen clock, fans every gesture out to all controllers in the same
 * call stack (so lanes can never move on different frames), and renders the
 * one-line controls row above the chart.
 *
 * The lane selection persists per controller (localStorage), so an
 * investigation survives a reload.
 */
@customElement('vitesse-controller-history')
export class ControllerHistorySection extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() controllerId = '';
  /**
   * Lanes and a window to open on, from a link or the assistant (router `parseControllerOpen`).
   * Applied once per arrival, through the same two moves a person makes — a combo and a range —
   * so what it shows is exactly what clicking would have shown, and it is persisted the same way.
   */
  @property({ attribute: false }) open?: ControllerOpen & { seq: number };

  @state() private customOpen = false;
  @state() private exportOpen = false;
  @state() private exporting = false;
  @state() private exportError = '';
  @state() private exportSucceeded = false;
  private exportCloseTimer?: ReturnType<typeof setTimeout>;
  /** When the failed state STARTED (null = healthy). The error chip only shows
   *  after the failure has persisted FAILED_CHIP_DELAY_MS — a single timed-out
   *  query that recovers on its 2s retry shouldn't flash red mid-gesture. */
  private failedSinceMs: number | null = null;
  private failedChipTimer?: ReturnType<typeof setTimeout>;
  @state() private pickerOpen = false;
  @state() private combosOpen = false;
  /** Lane order = chip order = slot-color order. */
  @state() private symbols: string[] = [];
  /** Earliest recorded instant for this controller (device-wide), or null when
   *  unknown/none — feeds the chart's "data begins" boundary so a preset wider
   *  than the device's history explains its own blank left side. Decoration:
   *  a failed bounds fetch just leaves it null and the tab renders as before. */
  @state() private dataStartMs: number | null = null;
  /** Guards a landing bounds fetch against a device switch mid-flight. */
  private boundsSeq = 0;

  /** One data plane per symbol, keyed by path. */
  private readonly vcs = new Map<string, HistoryViewportController>();
  /** The SHARED window — every controller mirrors it (single funnel below). */
  private viewport: TimelineViewport = { startMs: Date.now() - DEFAULT_SPAN_MS, endMs: Date.now() };
  /** The SHARED frozen clock (see HistoryViewportController.anchorNowMs) —
   *  every controller gets this same stamp, so all lanes clamp identically. */
  private anchorMs = Date.now();
  /** Carries an explicit range-selection warmup into lanes added afterward. */
  private viewportCameFromSelection = false;

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM, matching the other controller tab sections
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.rebuild();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.disposeAll();
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('backend') || changed.has('gatewayId') || changed.has('controllerId')) {
      this.rebuild();
    }
    // After any rebuild, so the arrival lands on the controller it was meant for.
    if (changed.has('open') && this.open && this.backend && this.gatewayId && this.controllerId) {
      if (this.open.symbols.length) this.applyCombo(this.open.symbols);
      if (this.open.minutes) this.showLast(this.open.minutes * 60_000);
    }
    // Feed the chart's real plot width to every lane's ladder once laid out.
    const chart = this.querySelector<VitesseTimelineChart>('vitesse-timeline-chart');
    if (chart && chart.plotWidth > 0) for (const vc of this.vcs.values()) vc.setPlotWidth(chart.plotWidth);
  }

  private disposeAll(): void {
    for (const vc of this.vcs.values()) vc.dispose();
    this.vcs.clear();
    clearTimeout(this.failedChipTimer);
    this.failedSinceMs = null;
  }

  private storageKey(): string {
    return `historySymbols:${this.gatewayId}:${this.controllerId}`;
  }

  /** The persisted selection, validated — garbage in storage must never crash
   *  the tab or spawn more lanes than the budget. */
  private loadSymbols(): string[] {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set<string>();
      const out: string[] = [];
      for (const p of parsed) {
        if (typeof p !== 'string' || !p || seen.has(p)) continue;
        seen.add(p);
        out.push(p);
        if (out.length === MAX_HISTORY_SYMBOLS) break;
      }
      return out;
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(this.symbols));
    } catch {
      // Storage full/blocked — the selection still works for this session.
    }
  }

  private rebuild(): void {
    this.disposeAll();
    this.symbols = [];
    if (!this.backend || !this.gatewayId || !this.controllerId) return;
    this.anchorMs = Date.now();
    this.viewport = { startMs: this.anchorMs - DEFAULT_SPAN_MS, endMs: this.anchorMs };
    this.symbols = this.loadSymbols();
    for (const path of this.symbols) this.spawn(path);
    void this.fetchBounds();
    this.requestUpdate();
  }

  /** Refresh the controller's recorded-data bounds (worker-cached, so repeat
   *  calls are free). Sequence-stamped: a slow reply from a previous device
   *  must never stamp its boundary onto the current one. */
  private async fetchBounds(): Promise<void> {
    if (!this.backend) return;
    const seq = ++this.boundsSeq;
    this.dataStartMs = null;
    const bounds = await this.backend.api.historyBounds(this.gatewayId, this.controllerId);
    if (seq !== this.boundsSeq) return;
    this.dataStartMs = bounds?.hasData ? bounds.minTsMs : null;
  }

  /** Create one lane's data plane, inheriting the SHARED window and clock so
   *  a lane added mid-view lands exactly on what's already on screen. */
  private spawn(path: string): void {
    if (!this.backend) return;
    const vc = new HistoryViewportController(
      this.backend,
      this.gatewayId,
      this.controllerId,
      path,
      this.viewport,
      this.anchorMs,
    );
    vc.onUpdate = () => this.requestUpdate();
    this.vcs.set(path, vc);
    if (this.viewportCameFromSelection) vc.setViewport(this.viewport.startMs, this.viewport.endMs, 'selection');
    else vc.refresh();
  }

  // ── lane assembly ──────────────────────────────────────────────────────────

  private addSymbol(path: string): void {
    if (!path || this.symbols.includes(path) || this.symbols.length >= MAX_HISTORY_SYMBOLS) return;
    this.symbols = [...this.symbols, path];
    this.persist();
    this.spawn(path);
  }

  private removeSymbol(path: string): void {
    this.vcs.get(path)?.dispose();
    this.vcs.delete(path);
    this.symbols = this.symbols.filter((s) => s !== path);
    this.persist();
  }

  /** Replace the lane set with a saved combo's paths, wholesale: kept lanes'
   *  data planes survive untouched, dropped ones are disposed, new ones spawn
   *  into the SHARED window/clock — so applying a combo loads immediately by
   *  the same machinery as adding lanes one by one. Persisted like any manual
   *  assembly, so the applied combo survives a reload. */
  private applyCombo(paths: string[]): void {
    const seen = new Set<string>();
    const next: string[] = [];
    for (const p of paths) {
      if (!p || seen.has(p)) continue;
      seen.add(p);
      next.push(p);
      if (next.length === MAX_HISTORY_SYMBOLS) break;
    }
    for (const p of this.symbols) {
      if (!seen.has(p)) {
        this.vcs.get(p)?.dispose();
        this.vcs.delete(p);
      }
    }
    this.symbols = next;
    this.persist();
    for (const p of next) if (!this.vcs.has(p)) this.spawn(p);
  }

  // ── viewport plumbing (the SINGLE funnel — lanes move in lock-step) ────────

  /** Commit a window to every lane in one call stack, then adopt the clamped
   *  result (all controllers share the anchor, so all clamp identically). */
  private applyViewport(rawStartMs: number, rawEndMs: number, cause: ViewportChangeCause): void {
    this.viewportCameFromSelection = cause === 'selection';
    // Nothing before the recorded-data boundary, whichever way the window
    // moved (preset, absolute picker, or a drag/zoom that hit the wall).
    const { startMs, endMs } = clampToDataStart(rawStartMs, rawEndMs, this.dataStartMs, this.anchorMs);
    for (const vc of this.vcs.values()) vc.setViewport(startMs, endMs, cause);
    const first = this.vcs.values().next().value;
    if (first) {
      this.viewport = { ...first.viewport };
    } else {
      // No lanes yet — clamp locally so the chosen window survives until the
      // first symbol is added (its controller then inherits it via spawn()).
      const span = Math.min(MAX_VIEWPORT_SPAN_MS, Math.max(MIN_VIEWPORT_SPAN_MS, endMs - startMs));
      const e = Math.min(endMs, this.anchorMs);
      this.viewport = { startMs: e - span, endMs: e };
    }
    this.requestUpdate();
  }

  private onViewportChange = (e: CustomEvent<TimelineViewport>): void => {
    this.applyViewport(e.detail.startMs, e.detail.endMs, 'gesture');
  };

  private onPlotResize = (e: CustomEvent<{ width: number }>): void => {
    for (const vc of this.vcs.values()) vc.setPlotWidth(e.detail.width);
  };

  private applyPreset(id: string): void {
    const p = PRESETS.find((x) => x.id === id);
    // The pill is already disabled for a span past the device's lifespan;
    // this is the belt to that brace (a stale click during a device switch).
    if (!p || presetExceedsLifespan(p.spanMs, this.dataStartMs, Date.now())) return;
    this.showLast(p.spanMs);
  }

  /** The window `spanMs` long ending now — a preset's move, and an arrival's `minutes`. */
  private showLast(spanMs: number): void {
    // A deliberate range selection, so it may advance the frozen clock:
    // "last 15m" means the 15 minutes ending at THIS click. Gestures, by
    // contrast, can never move past the anchor. ONE stamp for every lane —
    // per-lane Date.now() calls would let clamps drift apart.
    const nowMs = Date.now();
    this.anchorMs = nowMs;
    for (const vc of this.vcs.values()) vc.reanchor(nowMs);
    this.applyViewport(nowMs - spanMs, nowMs, 'selection');
  }

  /** Which preset the current viewport IS (drag/zoom deselects — honest).
   *  Compared against the frozen anchor, not live now — otherwise the pill
   *  would silently deselect a minute after opening the tab with nothing moved. */
  private activePreset(): string {
    const v = this.viewport;
    const span = v.endMs - v.startMs;
    if (this.anchorMs - v.endMs > 1000) return 'custom';
    const hit = PRESETS.find((p) => Math.abs(span - p.spanMs) < p.spanMs * 0.01);
    return hit?.id ?? 'custom';
  }

  private applyCustomRange(): void {
    const from = this.querySelector<HTMLInputElement>('.controller-history-from');
    const to = this.querySelector<HTMLInputElement>('.controller-history-to');
    if (!from?.value || !to?.value) return;
    const startMs = new Date(from.value).getTime();
    const endMs = new Date(to.value).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return;
    this.customOpen = false;
    // An explicit ask for a range newer than the frozen clock advances it
    // (same deliberate-selection rule as presets); applyViewport then clamps.
    if (endMs > this.anchorMs) {
      const nowMs = Date.now();
      this.anchorMs = nowMs;
      for (const vc of this.vcs.values()) vc.reanchor(nowMs);
    }
    this.applyViewport(startMs, endMs, 'selection');
  }

  /** Export the dialog's (default: on-screen) range as CSV. The download runs
   *  on the main thread via the helper (the save picker lives there — the one
   *  IO the worker can't do); this component still never fetches itself. A
   *  refusal's prose (plan gate, bad range) lands in exportError verbatim —
   *  the server writes sentences, so the dialog shows them, not a code. */
  private async runExport(): Promise<void> {
    const from = this.querySelector<HTMLInputElement>('.controller-history-export-from');
    const to = this.querySelector<HTMLInputElement>('.controller-history-export-to');
    if (!this.backend || this.exporting) return;
    const startMs = from?.value ? new Date(from.value).getTime() : NaN;
    const endMs = to?.value ? new Date(to.value).getTime() : NaN;
    // A refused range must SAY so — a silent return here is a Download button
    // that "does nothing" (which is exactly how it was reported).
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      this.exportSucceeded = false;
      this.exportError = t('controller.historyExportBadRange');
      return;
    }
    this.exporting = true;
    this.exportError = '';
    this.exportSucceeded = false;
    try {
      // Export what's on screen: every assembled lane. The route pages each
      // symbol independently, so multi-lane exports stream fine.
      const downloaded = await downloadHistoryCsv(
        this.backend,
        this.gatewayId,
        this.controllerId,
        this.symbols,
        startMs,
        endMs,
      );
      // A dismissed save picker is a change of mind, not a success — leave the
      // dialog open with no message rather than claiming a download that never
      // happened.
      if (downloaded) {
        this.exportSucceeded = true;
        clearTimeout(this.exportCloseTimer);
        this.exportCloseTimer = setTimeout(() => {
          this.exportOpen = false;
          this.exportSucceeded = false;
        }, 1500);
      }
    } catch (err) {
      this.exportError = t('controller.historyExportFailed', { reason: (err as Error)?.message ?? String(err) });
    } finally {
      this.exporting = false;
    }
  }

  // ── render ─────────────────────────────────────────────────────────────────

  /** loading (nothing confirmed yet) / failed / empty (confirmed, no data) —
   *  three states the chart's veils alone can't narrate, aggregated across
   *  lanes (each lane's own veil already narrates its individual coverage). */
  private statusChip() {
    if (this.vcs.size === 0) return nothing;
    const v = this.viewport;
    let anyFailed = false;
    let anyUncovered = false;
    let anyVisible = false;
    let allVisibleCovered = true;
    for (const vc of this.vcs.values()) {
      if (vc.fetchFailed) anyFailed = true;
      const d = vc.timelineData;
      if (!d.covered) {
        anyUncovered = true;
        allVisibleCovered = false;
        continue;
      }
      if (!(d.covered.startMs <= v.startMs + 1 && d.covered.endMs >= v.endMs - 1)) allVisibleCovered = false;
      // One lookup, not a walk over every resident bucket — this runs on every
      // render, which during a gesture is every frame.
      const i = lowerBound(d.buckets, v.startMs);
      if (i < d.buckets.length && d.buckets[i].tsMs <= v.endMs) anyVisible = true;
    }
    if (anyFailed) {
      if (this.failedSinceMs === null) {
        this.failedSinceMs = Date.now();
        // Nothing re-renders on its own when the grace period lapses — poke one.
        clearTimeout(this.failedChipTimer);
        this.failedChipTimer = setTimeout(() => this.requestUpdate(), FAILED_CHIP_DELAY_MS + 50);
      }
      if (Date.now() - this.failedSinceMs >= FAILED_CHIP_DELAY_MS)
        return html`<span class="controller-history-status error">${t('controller.historyLoadFailed')}</span>`;
      // Within the grace period a young failure reads as still-loading.
      return html`<span class="controller-history-status">${t('controller.historyLoading')}</span>`;
    }
    this.failedSinceMs = null;
    if (anyUncovered) return html`<span class="controller-history-status">${t('controller.historyLoading')}</span>`;
    if (allVisibleCovered && !anyVisible)
      return html`<span class="controller-history-status">${t('controller.historyNoData')}</span>`;
    return nothing;
  }

  private laneTheme(i: number): ThemeOption {
    return LANE_THEMES[i] ?? LANE_THEMES[LANE_THEMES.length - 1];
  }

  /** The legend IS the assembly UI: one chip per lane (swatch + path + ×).
   *  Styled like the header's status badges (tinted background, solid text,
   *  no border, rectangular radius) instead of the generic outlined base-chip
   *  — each chip built by hand (not the shared chip() helper) so its tint can
   *  be the lane's own color, matching the swatch/lane it labels. */
  private renderChips() {
    return html`<span class="base-chips controller-history-chips">
      ${this.symbols.map((path, i) => {
        const line = CHART_THEMES[this.laneTheme(i)].line;
        return html`<span class="base-chip" style="background:${line}26;color:${line}"
          >${path}<button class="base-chip-x" title=${t('common.remove')} @click=${() => this.removeSymbol(path)}
            ><vitesse-icon name="trash-2" size="12"></vitesse-icon
          ></button>
        </span>`;
      })}
      ${button({
        label: t('controller.historyAddSymbol'),
        icon: 'plus',
        color: 'blue',
        size: 'sm',
        disabled: this.symbols.length >= MAX_HISTORY_SYMBOLS,
        title:
          this.symbols.length >= MAX_HISTORY_SYMBOLS
            ? t('controller.historyMaxSymbols', { max: MAX_HISTORY_SYMBOLS })
            : t('controller.historyAddSymbol'),
        onClick: () => (this.pickerOpen = true),
      })}
    </span>`;
  }

  render() {
    if (!this.backend || !this.gatewayId || !this.controllerId) {
      return html`<div class="controller-program-empty">${t('controller.historyEmpty')}</div>`;
    }
    const v = this.viewport;
    const lanes: TimelineLane[] = this.symbols.map((path, i) => ({
      symbolPath: path,
      data: this.vcs.get(path)?.timelineData ?? null,
      theme: this.laneTheme(i),
    }));
    return html`
      <div class="controller-history">
        <div class="controller-history-controls">
          <vitesse-pill-tabs
            .tabs=${PRESETS.map((p) => {
              const beyond = presetExceedsLifespan(p.spanMs, this.dataStartMs, this.anchorMs);
              return {
                id: p.id,
                label: t(p.labelKey),
                disabled: beyond,
                title: beyond
                  ? t('controller.historyRangeBeyondLifespan', {
                      lifespan: fmtDuration(this.anchorMs - (this.dataStartMs ?? this.anchorMs)),
                    })
                  : undefined,
              };
            })}
            .active=${this.activePreset()}
            .onChange=${(id: string) => (id === 'custom' ? (this.customOpen = true) : this.applyPreset(id))}
          ></vitesse-pill-tabs>
          <span class="controller-history-controls-right">
            ${button({
              label: t('controller.historyExport'),
              icon: 'download',
              color: 'blue',
              size: 'sm',
              disabled: this.symbols.length === 0, // nothing on screen ⇒ nothing to export
              title:
                this.symbols.length === 0
                  ? t('controller.historyNoSymbols')
                  : t('controller.historyExport'),
              onClick: () => {
                clearTimeout(this.exportCloseTimer);
                this.exportError = '';
                this.exportSucceeded = false;
                this.exportOpen = true;
              },
            })}
            ${button({
              label: t('controller.historyCombos'),
              icon: 'layers', // no bookmark glyph in the registry; layers reads as "a set of lanes"
              color: 'blue',
              size: 'sm',
              onClick: () => (this.combosOpen = true),
            })}
          </span>
        </div>
        <div class="controller-history-body">
          <div class="controller-history-chips-row">${this.renderChips()} ${this.statusChip()}</div>
          ${this.symbols.length === 0
            ? html`<div class="controller-history-empty">
                <span>${t('controller.historyNoSymbols')}</span>
                ${button({
                  label: t('controller.historyAddSymbol'),
                  icon: 'plus',
                  color: 'blue',
                  size: 'sm',
                  onClick: () => (this.pickerOpen = true),
                })}
              </div>`
            : html`<div class="controller-history-chartwrap">
                <vitesse-timeline-chart
                  .viewport=${v}
                  .lanes=${lanes}
                  .maxEndMs=${this.anchorMs}
                  .dataStartMs=${this.dataStartMs}
                  @viewport-change=${this.onViewportChange}
                  @plot-resize=${this.onPlotResize}
                ></vitesse-timeline-chart>
              </div>`}
        </div>
      </div>
      <vitesse-history-symbol-picker
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .controllerId=${this.controllerId}
        .open=${this.pickerOpen}
        .taken=${this.symbols}
        @symbol-pick=${(e: CustomEvent<string>) => {
          this.addSymbol(e.detail);
          this.pickerOpen = false;
        }}
        @dialog-close=${() => (this.pickerOpen = false)}
      ></vitesse-history-symbol-picker>
      <vitesse-history-combo-dialog
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .controllerId=${this.controllerId}
        .open=${this.combosOpen}
        .currentSymbols=${this.symbols}
        @combo-apply=${(e: CustomEvent<string[]>) => {
          this.applyCombo(e.detail);
          this.combosOpen = false;
        }}
        @dialog-close=${() => (this.combosOpen = false)}
      ></vitesse-history-combo-dialog>
      <vitesse-dialog
        .open=${this.customOpen}
        heading=${t('controller.historyCustomTitle')}
        @dialog-close=${() => (this.customOpen = false)}
      >
        <div class="controller-history-range-form">
          <label>
            <span>${t('controller.historyCustomFrom')}</span>
            <input
              class="controller-history-from"
              type="datetime-local"
              step="1"
              min=${this.dataStartMs === null ? nothing : toLocalInputSec(this.dataStartMs)}
              .value=${toLocalInputSec(v.startMs)}
            />
          </label>
          <label>
            <span>${t('controller.historyCustomTo')}</span>
            <input class="controller-history-to" type="datetime-local" step="1" .value=${toLocalInputSec(v.endMs)} />
          </label>
        </div>
        <div slot="footer">
          ${button({ label: t('controller.historyApply'), color: 'blue', onClick: () => this.applyCustomRange() })}
        </div>
      </vitesse-dialog>
      <vitesse-dialog
        .open=${this.exportOpen}
        heading=${t('controller.historyExportTitle')}
        @dialog-close=${() => {
          clearTimeout(this.exportCloseTimer);
          this.exportOpen = false;
          this.exportSucceeded = false;
        }}
      >
        <!-- Prefilled with what's on screen; widening the range before download
             is exactly editing these two fields (the spec's export contract). -->
        <div class="controller-history-range-form">
          <!-- step="1" + seconds in the value: at sub-minute zoom, minute
               precision collapsed from == to and the export refused. -->
          <label>
            <span>${t('controller.historyCustomFrom')}</span>
            <input class="controller-history-export-from" type="datetime-local" step="1" .value=${toLocalInputSec(v.startMs)} />
          </label>
          <label>
            <span>${t('controller.historyCustomTo')}</span>
            <input class="controller-history-export-to" type="datetime-local" step="1" .value=${toLocalInputSec(v.endMs)} />
          </label>
        </div>
        <!-- One status line, one state at a time: running → spinner (a long
             export streams for a while and a dead-quiet dialog reads as hung),
             else success, else the failure prose. -->
        ${this.exporting
          ? html`<div class="controller-history-status exporting">
              <vitesse-icon name="refresh-cw" size="14" class="controller-history-export-spin"></vitesse-icon>
              ${t('controller.historyExportRunning')}
            </div>`
          : this.exportSucceeded
            ? html`<div class="controller-history-status success">${t('controller.historyExportSucceeded')}</div>`
            : this.exportError
              ? html`<div class="controller-history-status error">${this.exportError}</div>`
              : nothing}
        <div slot="footer">
          ${button({
            label: t('controller.historyExportStart'),
            color: 'blue',
            disabled: this.exporting,
            onClick: () => void this.runExport(),
          })}
        </div>
      </vitesse-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-controller-history': ControllerHistorySection;
  }
}
